import React from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Settings } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { ParsedLoadError } from '@/utils/productScope';
import { ProductOptionLabel } from '@/components/ProductOptionLabel';

export type ProductScopePanelVariant = 'select-product' | 'checking' | 'blocked' | 'empty';

type ProductScopeStatePanelProps = {
  variant: ProductScopePanelVariant;
  productId?: string;
  productName?: string | null | undefined;
  title: string;
  detail?: string | undefined;
  severity?: 'info' | 'warning' | 'error';
  onRetry?: () => void;
  showRegistryLink?: boolean;
};

export function errorToPanel(
  error: ParsedLoadError
): Pick<ProductScopeStatePanelProps, 'title' | 'detail' | 'severity'> {
  const severity =
    error.kind === 'misconfigured' || error.kind === 'forbidden'
      ? 'warning'
      : error.kind === 'unreachable'
        ? 'error'
        : 'error';
  return { title: error.title, detail: error.detail, severity };
}

export const ProductScopeStatePanel: React.FC<ProductScopeStatePanelProps> = ({
  variant,
  productId,
  productName,
  title,
  detail,
  severity = 'info',
  onRetry,
  showRegistryLink = true
}) => {
  const navigate = useNavigate();

  if (variant === 'checking') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const alertSeverity = variant === 'empty' ? 'info' : severity;

  return (
    <Box sx={{ py: 2 }}>
      <Alert severity={alertSeverity} sx={{ maxWidth: 720 }}>
        <Stack spacing={1}>
          {productId && (
            <Typography variant="body2" color="text.secondary">
              Produto: <ProductOptionLabel displayName={productName} productId={productId} />
            </Typography>
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {detail && (
            <Typography variant="body2" component="p" sx={{ whiteSpace: 'pre-wrap' }}>
              {detail}
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {onRetry && (
              <Button size="small" variant="outlined" onClick={onRetry}>
                Tentar novamente
              </Button>
            )}
            {showRegistryLink && productId && variant === 'blocked' && (
              <Button
                size="small"
                variant="text"
                startIcon={<Settings fontSize="small" />}
                onClick={() => navigate('/platform/products?tab=registry')}
              >
                Abrir registry do produto
              </Button>
            )}
          </Stack>
        </Stack>
      </Alert>
    </Box>
  );
};
